import { useState, type KeyboardEvent } from 'react';

import type { Approvals, PaperEdit } from './JournalPaper';
import type { RiskLibraryLine } from './riskLibraryApi';
import { RiskLibraryPicker } from './RiskLibraryPicker';
import { riskLevel, type JournalInput, type RiskItem } from './safeJournalApi';
import { blankRiskItem, RISK_KINDS, SHEET_STYLES } from './sheetModel';
import { GrowTextarea, SheetField, SheetHead } from './SheetParts';

/** 가능성 · 중대성 — 3단계. */
const LIKELIHOOD = [
  { value: 3, label: '3', title: '상' },
  { value: 2, label: '2', title: '중' },
  { value: 1, label: '1', title: '하' },
];
const SEVERITY = [
  { value: 3, label: '3', title: '대' },
  { value: 2, label: '2', title: '중' },
  { value: 1, label: '1', title: '소' },
];

/**
 * 위험성평가 한 벌(V572 · DOC-136) — 머리(구분 · 평가자)와 줄 표. 줄: 업체 · 공종 · 세부작업 · 위험요인 → 가능성 × 중대성 →
 * 감소대책 · 담당 · 조치예정 · 중점관리. 중점관리로 표시한 줄은 TBM 일지의 체크리스트로 따라간다.
 * 위험요인이 빈 줄은 서버가 버린다.
 */
/** 한 줄 값(업체 · 공종 · 세부작업 · 담당)은 칸에서 줄바꿈만 할 뿐 Enter 로 줄을 넣지 않는다. */
const oneLine = (e: KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key === 'Enter') e.preventDefault();
};

export function RiskSheet({
  journal,
  edit,
  approvals,
}: {
  journal: Omit<JournalInput, 'siteId'>;
  edit: PaperEdit;
  approvals?: Approvals;
}) {
  const items = journal.riskItems ?? [];
  const set = (at: number, next: Partial<RiskItem>) =>
    edit({ riskItems: items.map((row, index) => (index === at ? { ...row, ...next } : row)) });
  const text = (value: string) => (value === '' ? null : value);
  const keyCount = items.filter((row) => row.keyPoint).length;
  const [library, setLibrary] = useState(false);

  // 표준 목록의 줄 → 표 끝에. 옛 가능성 · 중대성은 같은 1~3 이라 그대로 옮긴다. 업체는 마지막 줄을 따른다.
  // 아무것도 안 적은 줄(서버가 버릴 줄)은 걷고 붙인다.
  const addFromLibrary = (lines: RiskLibraryLine[]) => {
    const kept = items.filter((row) => row.hazard.trim() || row.task || row.measure || row.vendorName || row.workType);
    const vendorName = items[items.length - 1]?.vendorName ?? null;
    const added = lines.map<RiskItem>((line) => ({
      ...blankRiskItem({ vendorName }),
      workType: line.trade,
      task: line.task,
      hazard: line.hazard,
      measure: line.measure,
      likelihood: line.likelihood ?? null,
      severity: line.severity ?? null,
    }));
    edit({ riskItems: [...kept, ...added] });
    setLibrary(false);
  };

  return (
    <div className="ss-sheet" style={{ minWidth: '980px' }}>
      <style>{SHEET_STYLES}</style>
      <SheetHead title="위험성평가" approvals={approvals} />

      <div className="ss-fields">
        <SheetField label="평가 구분">
          <select className="input" value={journal.title ?? ''} onChange={(e) => edit({ title: text(e.target.value) })}>
            <option value="">—</option>
            {RISK_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind} 위험성평가
              </option>
            ))}
          </select>
        </SheetField>
        <SheetField label="평가자">
          <input className="input" value={journal.inspector ?? ''} onChange={(e) => edit({ inspector: text(e.target.value) })} />
        </SheetField>
      </div>

      <div className="ss-section">
        <span>
          위험요인 및 감소대책 <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· 중점관리 {keyCount}건</span>
        </span>
        <span style={{ display: 'inline-flex', gap: '6px' }}>
          <button type="button" className="btn-secondary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={() => setLibrary(true)}>
            표준 목록에서 불러오기
          </button>
          <button
            type="button"
            className="btn-secondary"
            style={{ fontSize: '12px', padding: '4px 10px' }}
            onClick={() => edit({ riskItems: [...items, blankRiskItem(items[items.length - 1])] })}
          >
            + 줄 추가
          </button>
        </span>
      </div>
      {library && <RiskLibraryPicker onPick={addFromLibrary} onClose={() => setLibrary(false)} />}
      <table className="ss-grid">
        <colgroup>
          {/* 감소대책이 가장 긴 글이라 남는 폭을 다 가져간다(위험요인은 고정) — 반씩 나누면 한 줄이 대여섯 줄로 늘어난다. */}
          <col style={{ width: '34px' }} />
          <col style={{ width: '8%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '56px' }} />
          <col style={{ width: '56px' }} />
          <col style={{ width: '64px' }} />
          <col />
          <col style={{ width: '6%' }} />
          <col style={{ width: '112px' }} />
          <col style={{ width: '42px' }} />
          <col style={{ width: '32px' }} />
        </colgroup>
        <thead>
          <tr>
            <th>No</th>
            <th>업체</th>
            <th>공종</th>
            <th>세부작업</th>
            <th>위험요인</th>
            <th title="3 상 · 2 중 · 1 하">가능성</th>
            <th title="3 대 · 2 중 · 1 소">중대성</th>
            <th>위험성</th>
            <th>감소대책</th>
            <th>담당</th>
            <th>조치예정</th>
            <th>중점</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((row, at) => {
            const level = riskLevel(row);
            return (
              <tr key={row.id ?? `new-${at}`}>
                <td className="ss-c">{at + 1}</td>
                <td>
                  <GrowTextarea className="ss-cell" value={row.vendorName ?? ''} onKeyDown={oneLine} onChange={(e) => set(at, { vendorName: text(e.target.value.replace(/\n/g, ' ')) })} />
                </td>
                <td>
                  <GrowTextarea className="ss-cell" value={row.workType ?? ''} onKeyDown={oneLine} onChange={(e) => set(at, { workType: text(e.target.value.replace(/\n/g, ' ')) })} />
                </td>
                <td>
                  <GrowTextarea className="ss-cell" value={row.task ?? ''} onKeyDown={oneLine} onChange={(e) => set(at, { task: text(e.target.value.replace(/\n/g, ' ')) })} />
                </td>
                <td>
                  <GrowTextarea className="ss-cell" value={row.hazard} onChange={(e) => set(at, { hazard: e.target.value })} />
                </td>
                <td>
                  <select className="ss-cell" value={row.likelihood ?? ''} onChange={(e) => set(at, { likelihood: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">—</option>
                    {LIKELIHOOD.map((one) => (
                      <option key={one.value} value={one.value} title={one.title}>
                        {one.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select className="ss-cell" value={row.severity ?? ''} onChange={(e) => set(at, { severity: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">—</option>
                    {SEVERITY.map((one) => (
                      <option key={one.value} value={one.value} title={one.title}>
                        {one.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="ss-c">{level ? <span className={`badge ${level.tone}`}>{`${level.score} ${level.label}`}</span> : '—'}</td>
                <td>
                  <GrowTextarea className="ss-cell" value={row.measure ?? ''} onChange={(e) => set(at, { measure: text(e.target.value) })} />
                </td>
                <td>
                  <GrowTextarea className="ss-cell" value={row.owner ?? ''} onKeyDown={oneLine} onChange={(e) => set(at, { owner: text(e.target.value.replace(/\n/g, ' ')) })} />
                </td>
                <td>
                  <input type="date" className="ss-cell" value={row.dueDate ?? ''} onChange={(e) => set(at, { dueDate: text(e.target.value) })} />
                </td>
                <td className="ss-c">
                  <input type="checkbox" checked={row.keyPoint} onChange={(e) => set(at, { keyPoint: e.target.checked })} aria-label="중점관리" />
                </td>
                <td className="ss-c">
                  <button type="button" className="ss-x" aria-label="줄 지우기" onClick={() => edit({ riskItems: items.filter((_, index) => index !== at) })}>
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="ss-section">비고</div>
      <textarea className="input" rows={3} style={{ width: '100%' }} value={journal.content ?? ''} onChange={(e) => edit({ content: text(e.target.value) })} />
    </div>
  );
}
